/**
 * Auto-generated DTOs and enums based on OpenAPI schemas provided.
 * These types mirror the backend contracts and should remain in sync
 * with the API. Prefer composing app-level view models elsewhere.
 */
import {
  ResearchPhaseDto,
  NotaryFollowUpDto,
  EggFranceFollowUpDto,
  EggInternationalFollowUpDto,
} from './tracking-research.dto'

export { ResearchPhaseDto, NotaryFollowUpDto, EggFranceFollowUpDto, EggInternationalFollowUpDto }

// Enums
export enum AlertCategory {
  System = 0,
  File = 1,
  User = 2,
  Security = 3,
  Other = 4,
}

export enum AlertSeverity {
  Info = 0,
  Warning = 1,
  Critical = 2,
}

export enum AlertType {
  Type0 = 0,
  Type1 = 1,
  Type2 = 2,
  Type3 = 3,
  Type4 = 4,
  Type5 = 5,
  Type6 = 6,
}

export enum AlertNotificationType {
  DocumentDepose = 0,
  SuiviReglement = 1,
  SuiviRecherche = 2,
  NouvellesConnexions = 3,
  PiecesEnAttente = 4,
  DevisEnAttente = 5,
  DossierAAttribuer = 6,
  DevisInternationalEnAttente = 7,
}

export enum AlertNotificationCategory {
  NouveauDocument = 0,
  NouvelleInfo = 1,
  DossierAttribuer = 2,
  DevisEnAttente = 3,
}

export const AlertNotificationCategoryLabel: Record<string, string> = {
  NouveauDocument: 'Documents déposés',
  NouvelleInfo: 'Informations disponibles',
  DossierAttribuer: 'Dossiers à attribuer',
  DevisEnAttente: 'Demandes de devis en attente',
}

export function getAlertCategoryLabel(categoryName: string): string {
  return AlertNotificationCategoryLabel[categoryName] ?? categoryName
}

export const AlertNotificationTypeLabel: Record<AlertNotificationType, [singular: string, plural: string]> = {
  [AlertNotificationType.DocumentDepose]: ['Actif déposé', 'Actifs déposés'],
  [AlertNotificationType.SuiviReglement]: ['Changement de suivi de règlement', 'Changements de suivi de règlement'],
  [AlertNotificationType.SuiviRecherche]: ['Changement de suivi de recherche', 'Changements de suivi de recherche'],
  [AlertNotificationType.NouvellesConnexions]: ['Nouvelle connexion client', 'Nouvelles connexions clients'],
  [AlertNotificationType.PiecesEnAttente]: ['Demande de pièce en attente', 'Demandes de pièces en attente'],
  [AlertNotificationType.DevisEnAttente]: ['Devis à traiter', 'Devis à traiter'],
  [AlertNotificationType.DossierAAttribuer]: ['Dossier à attribuer', 'Dossiers à attribuer'],
  [AlertNotificationType.DevisInternationalEnAttente]: [
    'Devis international à traiter',
    'Devis internationaux à traiter',
  ],
}

export function getAlertNotificationTypeLabel(type: AlertNotificationType, count: number): string {
  const labels = AlertNotificationTypeLabel[type]
  return labels ? (count > 1 ? labels[1] : labels[0]) : ''
}

export enum Role {
  Role0 = 0,
  Role1 = 1,
  Role2 = 2,
  Role3 = 3,
  Role4 = 4,
  Role5 = 5,
}

export enum StepFileTracking {
  Step0 = 0,
  Step1 = 1,
  Step2 = 2,
  Step3 = 3,
  Step4 = 4,
  Step5 = 5,
}

export enum SuccessionPhase {
  Phase1 = 1,
  Phase2 = 2,
  Phase3 = 3,
  Phase4 = 4,
  Phase5 = 5,
  Phase6 = 6,
}

// Common types
export interface ErreurDataValidation {
  idChamp: number
  libelleChamp: string | null
  message: string | null
}

export interface ApiFailure {
  status: string | null
  message: string | null
  details: string | null
  correlationId: string | null
  errors: ErreurDataValidation[] | null
}

export interface ProblemDetails {
  type?: string | null
  title?: string | null
  status?: number | null
  detail?: string | null
  instance?: string | null

  // additionalProperties allowed
  [key: string]: unknown
}

// Alerts
export interface AlertDto {
  id: string // uuid
  successionFileId: number
  category: AlertCategory
  type: AlertType
  severity: AlertSeverity
  title: string | null
  message: string | null
  payload: string | null // JSON string
  role: Role
  createdAt: string // date-time
  readAt: string | null // date-time
  expiresAt: string | null // date-time
}

export interface HasNotificationDto {
  successionFileId: number
  hasNotification: boolean
  unreadCount: number
}

export interface AlertCountsDeceasedDto {
  id: number
  eggId: string
  lastName: string | null
  firstName: string | null
  name: string | null
  gender: GenderDto
  placeOfBirth: string | null
  city: string | null
  country: string | null
  dateOfDeath: string | null // date
}

export interface AlertCountsSuccessionFileDto {
  id: number
  eggId: string
  code: string
  createdAt: string // date-time
  modifiedAt: string // date-time
  status: StatusDto
  phase: PhaseDto
  deceased: AlertCountsDeceasedDto
}

export interface AlertCountsTypeDto {
  type: number
  typeName: string
  count: number
  lastAlertDate: string // date-time
}

export interface AlertCountsDto {
  successionFile: AlertCountsSuccessionFileDto
  category: number
  categoryName: string
  types: AlertCountsTypeDto[]
}

export interface AlertCategoryFolderDto {
  successionFileId: string
  lastAlertDate: string // date-time
  successionFile: AlertCountsSuccessionFileDto
  types: AlertCountsTypeDto[]
}

export interface AlertFolderCountsDto {
  category: number
  categoryName: string
  successionFiles: AlertCategoryFolderDto[]
}

// People metadata DTOs
export interface GenderDto {
  code: number
  name: string | null
  label: string | null
}

export interface MaritalStatusDto {
  code: number
  name: string | null
  label: string | null
}

export interface InternationalLawMotiveDto {
  code: string | null
  name: string | null
  label: string | null
}

export interface InternationalLawInfoDto {
  id: number
  deceasedId: number
  maidenName: string | null
  motherFullName: string | null
  foreignIdCardNumber: string | null
  entryDateInFrance: string | null // date
  isRegisteredAtConsulate: boolean | null
  consulateAddress: string | null
  motives: InternationalLawMotiveDto[] | null
  civilStatusDocuments: Array<string | number> | null
  lastKnownAddressInBirthCountry: string | null
  annexPieces: Array<string | number> | null
  foreignTaxIdNumber: string | null
  uploadedFiles: UploadedFileDto[] | null
  nextOfKinContact: string | null
  notaryNote: string | null
}

export interface UploadedFileDto {
  id: number
  internationalLawInfoId: number | null
  fileName: string | null
  filePath: string | null
  mimeType: string | null
  fileSize: number | null
  uploadedAt: string | null
  uploadedBy: string | null // date-time
}

export interface DeceasedDto {
  eggId: string
  deceasedId: number | null
  lastName: string | null
  firstName: string | null
  firstNames: string | null
  nationality: string | null
  gender: GenderDto
  profession: string | null
  socialSecurityNumber: string | null
  birthDate: string | null // date
  placeOfBirth: string | null
  dateOfDeath: string | null // date
  placeOfDeath: string | null
  lastResidence: string | null
  country: string | null
  city: string | null
  email: string | null
  phone: string | null
  landlinePhone: string | null
  workPhone: string | null
  profilePhoto: string | null
  maritalStatus: MaritalStatusDto
  spouseFullName: string | null
  marriageDate: string | null // date
  marriagePlace: string | null
  matrimonialRegime: string | null
  matrimonialRegimeModified: string | null
  underProtectionMeasure: boolean | null
  hasForeignElement: boolean | null
  notaryNotes: string | null
  internationalLawInfo: InternationalLawInfoDto | null
  documents: DeceasedDocumentDto[] | null
  createdAt: string | null // date-time
  modifiedAt: string | null // date-time
  createdBy: number | null
  modifiedBy: number | null
  notaryOfficeId: number | null
  successionFileId: number | null
}

// export type DeceasedDetailsDto = Omit<DeceasedDto, 'internationalLawInfo' | 'documents'>
export interface DeceasedDetailsDto {
  id: number
  deceasedId: number
  lastName: string | null
  firstName: string | null
  firstNames: string | null
  nationality: string | null
  gender: GenderDto
  profession: string | null
  socialSecurityNumber: string | null
  birthDate: string | null // date
  placeOfBirth: string | null
  dateOfDeath: string | null // date
  placeOfDeath: string | null
  lastResidence: string | null
  country: string | null
  city: string | null
  email: string | null
  phone: string | null
  landlinePhone: string | null
  workPhone: string | null
  profilePhoto: string | null
  maritalStatus: MaritalStatusDto
  spouseFullName: string | null
  marriageDate: string | null // date
  marriagePlace: string | null
  matrimonialRegime: string | null
  matrimonialRegimeModified: boolean | null
  underProtectionMeasure: boolean | null
  hasForeignElement: boolean | null
  notaryNotes: string | null
  createdAt: string | null // date-time
  modifiedAt: string | null // date-time
  createdBy: number | null
  modifiedBy: number | null
  notaryOfficeId: number | null
  successionFileId: number | null
}

export interface DeceasedDocumentDto {
  id: number
  deceasedId: number
  fileName: string | null
  filePath: string | null
  mimeType: string | null
  fileSize: number | null
  fileId: string | null
  fileType: string | null
  uploadedAt: string | null // date-time
  uploadedBy: number | null
  origin: StatusDto
  type: StatusDto
}

export interface InternationalLawInfoCreateUpdateRequest {
  maidenName?: string | null
  motherFullName?: string | null
  foreignIdCardNumber?: string | null
  entryDateInFrance?: string | null // date
  isRegisteredAtConsulate?: boolean | null
  consulateAddress?: string | null
  motives?: string[] | null
  civilStatusDocuments?: Array<string | number> | null
  lastKnownAddressInBirthCountry?: string | null
  annexPieces?: Array<string | number> | null
  foreignTaxIdNumber?: string | null
  uploadedFiles?: UploadedFileDto[] | null
  nextOfKinContact?: string | null
  notaryNote?: string | null
}

// Notary and office DTOs
export interface NotaryDto {
  id: number
  lastName: string | null
  firstName: string | null
  nationality: string | null
  birthDate: string | null // date
  country: string | null
  city: string | null
  socialSecurityNumber: string | null
  maritalStatus: MaritalStatusDto
  email: string | null
  phone: string | null
  createdAt: string | null // date-time
  modifiedAt: string | null // date-time
  createdBy: number | null
  modifiedBy: number | null
  gender: GenderDto
  landlinePhone: string | null
  workPhone: string | null
  profilePhoto: string | null
  profession: string | null
  codeCsn: string | null
  inscriptionDate: string | null // formatted string
  registrationDate: string // date-time
  actif: boolean
}

export interface StatusDto {
  code: number
  name: string | null
  label: string | null
}

export interface PhaseDto {
  code: number
  codeEgg: string | null
  name: string | null
  label: string | null
}

// todo: check required fields
export interface SuccessionFilePersonDto {
  id: number
  eggId: string
  sub: string
  name: string
  email: string
  nationality: string
  birthDate: string
  country: string
  city: string
  phone: string
  createdAt: string
  modifiedAt: string
  createdBy: number
  modifiedBy: number
  gender: number
  landlinePhone: string
  workPhone: string
  profilePhoto: string
  profession: string
  actif?: boolean
  role?: number
  lastConnexionAt?: string
}

export interface SuccessionFileSuccessor {
  id: number
  lastName: string
  firstName: string
  nationality: string
  birthDate: string
  country: string
  city: string
  socialSecurityNumber: string
  maritalStatus: MaritalStatusDto
  email: string
  phone: string
  createdAt: string
  modifiedAt: string
  createdBy: number
  modifiedBy: number
  gender: GenderDto
  landlinePhone: string
  workPhone: string
  profilePhoto: string
  profession: string
  deceasedId: number
  successionFileId: number
}
export interface FolderTypeDto {
  id: number
  successionFileId: string
  missionType: string
  realEstate: boolean
  lifeInsurance: boolean
  international: boolean
}
/** Succursale / office notarial rattaché au dossier (données EGG2). */
export interface SuccursaleDto {
  eggId: string | null
  name: string | null
  code: string | null
}

/** Notaire ayant accès au dossier (données EGG2 : identifiant + nom affichable). */
export interface NotaryAccessDto {
  eggId: string | null
  name: string | null
}

/** Type de document EGG (paramètre documentType) pour le sélecteur de la modale d'upload. */
export interface DocumentTypeOptionDto {
  id: string | null
  code: string | null
  name: string | null
}

/** Document renvoyé par le backend après upload. */
export interface UploadedDocumentDto {
  id: string | null
  name: string | null
}

/** Enveloppe de réponse pour la liste des types de documents. */
export interface DocumentTypeOptionsApiResponse {
  message?: string
  data: DocumentTypeOptionDto[] | null
}

/** Enveloppe de réponse après upload d'un document. */
export interface UploadDocumentApiResponse {
  message?: string
  data: UploadedDocumentDto | null
}

export interface SuccessionFileDto {
  eggId: string
  code: string
  /** Nom du dossier (libellé EGG2 du folder). */
  name: string | null
  successionFileId: number | null
  openedAt: string | null // date-time
  closedAt: string | null // date-time
  digitalDeathRequestAt: string | null // date-time
  digitalDeathClosedAt: string | null // date-time
  status: StatusDto
  phase: PhaseDto
  createdAt: string | null // date-time
  createdBy: number | null // int32
  modifiedAt: string | null // date-time
  modifiedBy: number | null // int32
  notaryOfficeId: number | null
  /** Succursale / office notarial (issu du détail EGG2). */
  office?: SuccursaleDto | null
  successorId: number | null
  deceased: DeceasedDto
  notaries: NotaryDto[] | null
  /** Notaires ayant accès au dossier (issus du détail EGG2). */
  notaryAccesses?: NotaryAccessDto[] | null

  prospecteurId: number
  chercheurId: number
  directeurId: number
  prospecteur: SuccessionFilePersonDto
  chercheur: SuccessionFilePersonDto[]
  directeur: SuccessionFilePersonDto
  regleur: SuccessionFilePersonDto
  notaryIds: number[]
  notaryIdsWaitingValidation: number[]
  successors: SuccessionFileSuccessor[]
  successorsWaitingValidation: SuccessionFileSuccessor[]
  folderType: FolderTypeDto
  notes: string | null
}

export interface NotaryOfficeDto {
  id: number
  notaryOfficeExternalId: number
  number: string | null
  lastName: string | null
  firstName: string | null
  department: string | null
  status: number
  deceasedId: number
  notaries: NotaryDto[] | null
  successionFiles: SuccessionFileDto[] | null
}

// File tracking
export interface FileTrackingDto {
  id: number
  successionFileId: number
  step: StepDto
  status: StepStatusDto
  comment: string | null
  lastUpdateDate: string // date-time
  createdDate: string // date-time
}

export interface StepDto {
  code: number
  name: string | null
  label: string | null
}

export interface StepStatusDto {
  code: number
  name: string | null
  label: string | null
}

// Auth
export interface LoginRequestDto {
  email: string | null
  password: string | null
}

// User
export interface UserDto {
  id: number
  firstName: string | null
  lastName: string | null
  email: string | null
  nationality: string | null
  birthDate: string | null // date
  country: string | null
  city: string | null
  phone: string | null
  createdAt: string | null // date-time
  modifiedAt: string | null // date-time
  createdBy: number | null
  modifiedBy: number | null
  gender: number | null // int32
  landlinePhone: string | null
  workPhone: string | null
  profilePhoto: string | null
  profession: string | null
  actif: boolean
  role: number // int32
  lastConnexionAt: string | null // date-time
}

export interface UserDtoApiOneSuccess {
  status: string | null
  message: string | null
  data: UserDto
}

export interface TrackingResearchDto {
  eggId: string
  successionFileReference: string
  deceased: DeceasedDto
  researchPhase: ResearchPhaseDto
  notaryFollowUp: NotaryFollowUpDto
  eggFranceFollowUp: EggFranceFollowUpDto
  eggInternationalFollowUp: EggInternationalFollowUpDto
}

// État des forces et charges
export interface DocumentNotaireVracDto {
  id: number
  displayName: string | null
  fileName: string | null
  fileType: string | null
  mimeType: string | null
  uploadedAt: string | null // date-time
  uploadedBy: string | null
  size: number | null
  url: string | null
}

export interface LigneActifDto {
  id: number
  libelle: string | null
  montant: number | null
  important?: boolean | null
}

export interface LignePassifDto {
  id: number
  libelle: string | null
  montant: number | null
  important?: boolean | null
}

export interface ActifNetDto {
  totalActifBrut: number | null
  totalPassif: number | null
  totalActifNetPrevisionnel: number | null
}

export interface EtatForcesChargesResponseDto {
  dossierSuccessionId: number
  documentsNotaireVrac: DocumentNotaireVracDto[] | null
  actif: LigneActifDto[] | null
  passif: LignePassifDto[] | null
  actifNet: ActifNetDto | null
  autresInformations: string | null
}

// Documents
export interface DocumentCategoryDto {
  code: number // int32
  name: string | null
  label: string | null
}

export interface DocumentDirectionDto {
  code: number // int32
  name: string | null
  label: string | null
}

export interface DocumentTypeDto {
  code: number // int32
  name: string | null
  label: string | null
}

export interface DocumentStatusDto {
  code: number // int32
  name: string | null
  label: string | null
}

// todo: check required fields
export interface DocumentDto {
  id: string | number // int64
  caseId?: number // int64
  personId?: number | null // int64
  displayName: string | null
  storagePath?: string | null
  filePath?: string | null
  category?: DocumentCategoryDto
  direction?: DocumentDirectionDto
  type?: DocumentTypeDto
  mimeType?: string | null
  status?: DocumentStatusDto
  sentAtUtc?: string | null // date-time
  receivedAtUtc?: string | null // date-time
  notes?: string | null
  createdAtUtc?: string // date-time
  updatedAtUtc?: string | null // date-time
}

export interface SuccessionFileAdditionalInfoDto {
  totalDocuments: number // int32
  lastDocumentUpdateDate: string | null // date-time
  documentCountByCategory: Record<string, number> | null // Map<string, int32>
}

export interface DocumentWithSuccessorDto {
  document: DocumentDto
  successorId: number // int64
  successorLastName: string | null
  successorFirstName: string | null
}

export interface SentByEggToSuccessorsDto {
  aTousLesHeritiers: DocumentDto[] | null
  auxDifferentsHeritiers: DocumentWithSuccessorDto[] | null
}

export interface ExchangeWithSuccessorsDto {
  piecesEnvoyeesParEgg: SentByEggToSuccessorsDto | null
  piecesRecuesDesHeritiers: DocumentDto[] | null
}

export interface PiecesActifsPassifsDto {
  envoyesParEgg: DocumentDto[] | null
  recusDuNotaire: DocumentDto[] | null
}

export interface SuccessionFileDocumentsDto {
  successionFileId: number // int64
  remiseAuNotaire: DocumentDto[] | null
  devisOuLettreDeMission: DocumentDto[] | null
  piecesDefunt: DocumentDto[] | null
  piecesHeritiers: DocumentDto[] | null
  echangesAvecHeritiers?: ExchangeWithSuccessorsDto | null // phase convention uniquement
  piecesActifsPassifs?: PiecesActifsPassifsDto | null // phase convention uniquement
  autresInformationsUtiles: SuccessionFileAdditionalInfoDto
}

export interface SuccessionFileDocumentsDtoApiOneSuccess {
  status: string | null
  message: string | null
  data: SuccessionFileDocumentsDto
}

// Document Requests (Demandes de documents)
export interface RoleDto {
  code: number // int32
  name: string | null
  label: string | null
}

export interface DemandeDocumentStatutDto {
  code: number // int32
  name: string | null
  label: string | null
}

export interface DemandeDocumentCreateRequest {
  dossierId: string // int64
  recipientRole?: Role | null
  message?: string | null
}

export interface DemandeDocumentResponse {
  id: number // int64
  dossierId: string // int64
  documentType: DocumentTypeDto
  statut: DemandeDocumentStatutDto
  message: string | null
  requestedByRole: RoleDto
  requestedByUserId: string // int64
  requestedByUserName: string | null
  recipientRole: RoleDto
  recipientUserId: string | null // int64
  recipientUserName: string | null
  createdAtUtc: string // date-time
  createdByUserId: string // int64
  lastModifiedAtUtc: string | null // date-time
  lastModifiedByUserId: string | null // int64
  statutChangedAtUtc: string | null // date-time
  statutChangedByUserId: string | null // int64
}

export interface DemandeDocumentResponseApiOneSuccess {
  status: string | null
  message: string | null
  data: DemandeDocumentResponse
}

export interface DemandeDocumentResponseApiListSuccess {
  status: string | null
  message: string | null
  data: DemandeDocumentResponse[] | null
  count: number // int32 (readonly)
}
export interface PaginationDto {
  pageNumber: number
  pageSize: number
  count: number
  countPages: number
  hasPreviousPage: boolean
  hasNextPage: boolean
}

export interface ApiPaginatedListResponseDto<T> {
  status: string
  message: string
  count: number
  pagination: PaginationDto
  data: T[]
}
export interface ApiListResponseDto<T> {
  status: string
  message: string
  count: number
  data: T[]
}

export interface SuccessorsInfoDto {
  id: number
  eggId: string
  lastName: string
  firstName: string
  birthDate: string
  placeOfBirth: string
  maritalStatus: StatusDto | null
  phone: string
  landlinePhone: string
  email: string
  quotePart: string
  relationshipWithDeceased: string
  city: string
  country: string
  isIdentified: boolean
}

export interface SuccessorDocumentsDto {
  piecesRecuesNotaire: DocumentDto[]
  comparutionsNotaire: DocumentDto[]
}

export interface SuccessorsDto {
  info: SuccessorsInfoDto
  notes: string
  documents: SuccessorDocumentsDto
}

export interface UpdateAvatarRequest {
  avatarUrl: string
}

export interface UpdateFolderTypeRequest {
  missionType: string | null
  realEstate: boolean
  lifeInsurance: boolean
  international: boolean
}
